import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDecision } from '@optimizely/react-sdk';
import clsx from 'clsx';
import { smallMobileStartBreakpoint } from '@/materialUi/theme';
import CenteredContainer from '../../../../common/CenteredContainer';
import fontStyles from '../../../../common/fontStyles';
import HeaderText from '../../../../common/HeaderText';
import BuilderSteps from '../../BuilderSteps';
import PizzaSummary from '../../steps/PizzaSummary';
import SpecialInstructionsSetterDesktop from '../../setters/SpecialInstructions/SpecialInstructionsSetterDesktop';
import { selectors } from '../../slice/pizza.slice';
import { 
  debouncedUpdateMeatToppings, 
  debouncedUpdateVeggieToppings, 
  debouncedUpdateFinishers,
  debouncedUpdateSize
} from '../../slice/debouncedActions';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import {
  EditBuilderSkeleton,
  useEditBuilderSkeleton
} from '@/common/EditBuilderSkeleton';
import { DealRecipe } from '@/builders/deals/slice/dealTypes';
import SizeCrustSetterDesktop from '@/builders/pizza/setters/SizeCrust/SizeCrustSetterDesktop';
import SauceCheeseFlavorSetterDesktop from '@/builders/pizza/setters/SauceCheeseFlavor/SauceCheeseFlavorSetter/SauceCheeseFlavorSetterDesktop';
import FinishersSetterDesktop from '@/builders/pizza/setters/Finishers/FinishersSetterDesktop/FinishersSetterDesktop';
import ToppingsPicker, { renderToppingExtraPriceText } from '@/builders/pizza/setters/Toppings/ToppingsPicker';
import { PizzaIngredient } from '@/builders/pizza/dataTransformers/builderTypes';
import { userSelectionsSelector } from '@/builders/deals/slice/userSelections.slice';
import useCrustSizeStepText from '../../steps/SizeCrust/useCrustSizeStepText';
import { usePizzaMeltDetector } from '@/hooks/usePizzaMeltDetector';
import { usePizzaPricing } from '@/clientCore/menu/hooks/usePizzaPricing';
import Routes from '@/router/routes';
import formattedPrice from '@/common/formattedPrice';
import Badge from '@/coreComponents/boxes/Badge';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '728px',
    flexGrow: 1,
    marginTop: '3.5em'
  },
  price: {
    ...fontStyles.h2Mobile,
    margin: '3px 23px 0 0'
  },
  builderStepContainerExpanded: {
    gap: '24px',
    '& *[role="radio"]': {
      margin: 0
    }
  },
  gridWidth: {
    width: '100%'
  },
  expandedCrust: {
    '& *[role="radiogroup"]': {
      justifyContent: 'start',
      gap: '24px',
      marginTop: '12px'
    }
  },
  expandedCrustHeading: {
    marginBottom: '24px',
    [theme.breakpoints.down(smallMobileStartBreakpoint)]: {
      marginTop: '24px'
    }
  },
  expandedToppings: {
    marginTop: '48px',
    maxWidth: '540px'
  },
  toppingsPriceSummary: {
    marginBottom: '24px'
  },
  meltSizeButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px'
  },
  meltSizeButton: {
    border: '2px solid transparent',
    borderRadius: '8px',
    boxShadow: '0px 3px 8px 0px #00000033',
    color: '#131313',
    height: '64px',
    textTransform: 'capitalize',
    minWidth: '104px',
    [theme.breakpoints.up('md')] : {
      width: '189px'
    }
  },
  meltSizeButtonSelected: {
    borderColor: '#E71316'
  },
  upchargeText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

export interface DesktopPizzaBuilderProps {
  step: BuilderSteps;
  setStep: (step: BuilderSteps) => void;
  currentPath?: string;
  recipes?: DealRecipe[];
  isEditFlow?: boolean;
}

interface MeltSizeWithUpchargeProps {
  name: string | undefined;
  id: string;
}

const PizzaBuilderDesktopExpanded = (
  props: DesktopPizzaBuilderProps
): JSX.Element => {
  const {
    step, setStep, currentPath, recipes, isEditFlow
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const summaryTitle = useSelector(selectors.selectPizzaSummaryTitle);
  const showEditBuilderSkeleton = useEditBuilderSkeleton();
  const isPizzaMelt = usePizzaMeltDetector();
  const selectedRecipe = useSelector(selectors.selectPizzaOptions);
  const currentStep = useSelector(userSelectionsSelector.selectCurrentStep);
  const { toppingUpChargePrice, numberOfIncludedToppings } = usePizzaPricing(currentPath);
  const [{ enabled: isDynamicFinishersSectionEnabled }] = useDecision('dtg-1723-dynamic-finishers-section');
  const [{ enabled: showMeltsSizes }] = useDecision('dtg-1596-melts-sizes-buttons');
  const [{ enabled: isShowDealPppContentEnabled }] = useDecision('fr-dtg-1770-show-deal-ppp-content');
  const [{ enabled: isShowMhbMeltUpchargeEnabled }] = useDecision('fr-ims-5-upcharge-badge-melts-mhb');

  const displayName = (isShowDealPppContentEnabled && currentStep?.customPageTitle) || 
    (recipes && recipes.length > 1 
      ? selectedRecipe?.summary.displayName 
      : summaryTitle);

  const toggleStep = (clickedStep: BuilderSteps) => {
    if (step === clickedStep) {
      setStep(BuilderSteps.MAIN);
    } else {
      setStep(clickedStep);
    }
  };

  const { subHeadingHasError, subHeading } = useCrustSizeStepText();
  const meatToppingsOptions = useSelector(selectors.selectMeatToppingOptions);
  const veggieToppingsOptions = useSelector(selectors.selectVeggieToppingsOptions);
  const selectedMeatToppings = useSelector(selectors.selectPizzaMeatToppings);
  const finisherOptions = useSelector(selectors.selectFinisherOptions);
  const selectedVeggieToppings = useSelector(selectors.selectPizzaVeggieToppings);
  const selectedCrust = useSelector(selectors.selectPizzaCrustOption);
  const selectedFinisher = useSelector(selectors.selectPizzaFinisher);
  const sizesOptions = useSelector(selectors.selectSizeOptions);
  const selectedPizzaSize = useSelector(selectors.selectPizzaSize);

  const handleAddMeatToppings = (toppings: PizzaIngredient[]) => {
    dispatch(debouncedUpdateMeatToppings(toppings));
  };

  const handleAddVeggieToppings = (toppings: PizzaIngredient[]) => {
    dispatch(debouncedUpdateVeggieToppings(toppings));
  };

  const handleVeggieAndFinisherUpdate = (toppings: PizzaIngredient[]) => {
    if (isDynamicFinishersSectionEnabled) {
      // When finishers are separated, only handle veggies
      dispatch(debouncedUpdateVeggieToppings(toppings));
    } else {
      // Legacy behavior: handle both veggies and finishers together
      const finishers = toppings.filter(topping => 
        finisherOptions.some(option => option.id === topping.id)
      );
      const veggies = toppings.filter(topping => 
        veggieToppingsOptions.some(option => option.id === topping.id)
      );
      if (finishers.length > 0) {
        dispatch(debouncedUpdateFinishers(finishers));
      }
      dispatch(debouncedUpdateVeggieToppings(veggies));
    }
  };
  
  const toppingExtraPriceText = renderToppingExtraPriceText(toppingUpChargePrice, numberOfIncludedToppings, selectedCrust, currentPath?.includes(Routes.DEALS));
  
  let veggieAndFinisherOptions = veggieToppingsOptions;
  let selectedVeggieAndFinishers = selectedVeggieToppings;
  let numVeggieAndFinisherItems = veggieToppingsOptions.length;

  if (!isDynamicFinishersSectionEnabled) {
    veggieAndFinisherOptions = [...veggieToppingsOptions, ...finisherOptions];
    selectedVeggieAndFinishers = [...selectedVeggieToppings, ...(selectedFinisher ? [selectedFinisher] : [])];
    numVeggieAndFinisherItems = veggieToppingsOptions.length + finisherOptions.length;
  }

  let veggieUpdateHandler = handleAddVeggieToppings;
  if (!isDynamicFinishersSectionEnabled) {
    veggieUpdateHandler = handleVeggieAndFinisherUpdate;
  }

  const MeltSizeWithUpcharge = ({ name, id }: MeltSizeWithUpchargeProps) => {
    const upcharge = selectedRecipe?.crusts?.find((crust) => crust?.sizeId === id)?.upcharge;
    const formattedUpcharge = upcharge ? `+${formattedPrice(upcharge)}` : '';

    return (
      <div className={classes.upchargeText}>
        <Typography>
          {name}
        </Typography>
        {formattedUpcharge && <Badge text={formattedUpcharge} ariaHidden />}
      </div>
    );
  };

  return (
    <CenteredContainer>
      <Grid className={classes.root} data-testid="build-your-pizza-desktop">
        <Grid item md={7}>
          <HeaderText
            testId='pizza-builder-header-text'
            text={displayName}
          />
          <PizzaSummary text="Build your pizza" noBorder />
        </Grid>

        {isPizzaMelt && sizesOptions.length > 1 && showMeltsSizes && (
          <Grid className={classes.meltSizeButtonsContainer} data-testid="melt-sizes-container">
            {sizesOptions.map((size) => (
              <Button
                disableRipple
                key={size.id}
                onClick={() => dispatch(debouncedUpdateSize(size))}
                className={clsx(classes.meltSizeButton, size.id === selectedPizzaSize.id && classes.meltSizeButtonSelected)}
              >
                {isShowMhbMeltUpchargeEnabled ? <MeltSizeWithUpcharge name={size.name} id={size.id} /> : size.name}
              </Button>
            ))}
          </Grid>
        )}
        <Grid container wrap="nowrap">
          <Grid item className={classes.gridWidth}>
            <Grid className={classes.builderStepContainerExpanded}>
              {showEditBuilderSkeleton ? (
                <EditBuilderSkeleton />
              ) : (
                <>
                  {!isPizzaMelt && (
                    <Grid className={classes.expandedCrust}>
                      <Grid className={classes.expandedCrustHeading}>
                        <HeaderText text="Crust" />
                        <Typography color={subHeadingHasError ? 'error' : undefined}>
                          {subHeading}
                        </Typography>
                      </Grid>
                      <SizeCrustSetterDesktop
                        toggleStep={toggleStep}
                        currentPath={currentPath}
                        isEditFlow={isEditFlow}
                      />
                    </Grid>
                  )}

                  <Grid className={classes.expandedToppings}>
                    <HeaderText text="Toppings" />
                    <Typography className={classes.toppingsPriceSummary} data-testid="toppings-extra-price">
                      {toppingExtraPriceText}
                    </Typography>
                    <ToppingsPicker
                      title="Meats"
                      toppingOptions={meatToppingsOptions}
                      selectedToppings={selectedMeatToppings}
                      onToppingsUpdate={handleAddMeatToppings}
                      extraPrice={toppingUpChargePrice}
                      numDisplayedItems={meatToppingsOptions.length}
                      hideToppingsExtraPrice
                      hideTitle
                    />
                    <ToppingsPicker
                      title="Veggies"
                      toppingOptions={veggieAndFinisherOptions}
                      selectedToppings={selectedVeggieAndFinishers}
                      onToppingsUpdate={veggieUpdateHandler}
                      extraPrice={toppingUpChargePrice}
                      numDisplayedItems={numVeggieAndFinisherItems}
                      hideToppingsExtraPrice
                      hideTitle
                    />
                  </Grid>

                  {isDynamicFinishersSectionEnabled && (
                    <Grid className={classes.expandedToppings}>
                      <FinishersSetterDesktop />
                    </Grid>
                  )}

                  <HeaderText margin="38px 0 24px" text="Sauce & cheese" />
                  <SauceCheeseFlavorSetterDesktop />
                </>
              )}
            </Grid>

            {!showEditBuilderSkeleton && <SpecialInstructionsSetterDesktop />}
            <CaloriesDisclaimer />
          </Grid>
        </Grid>
      </Grid>
    </CenteredContainer>
  );
};

export default PizzaBuilderDesktopExpanded;
